import { convertToTimezone } from '@awork/_shared/functions/date-operations'
import { isSameMonth, isSameYear, isWithinRange } from '@awork/_shared/functions/date-fns-wrappers'
import type { IRetainerBudget } from './retainer-budget.model'
import { RetainerBudget } from './retainer-budget.model'
import { normalizeRetainerDates, parseRetainerDate } from '../helpers/retainer-helper'

export interface IRetainer {
  id: string
  startDate: Date | string
  endDate?: Date | string
  timeBudget: number
  retainerPeriod: string
  timezone: string
  rollOverUnderspentBudget: boolean
  rollOverOverspentBudget: boolean
  projectId: string
  isExternal: boolean
  retainerBudgets: IRetainerBudget[]
  createdOn: Date | string
  createdBy: string
  updatedOn: Date | string
  updatedBy: string
  deleteddOn?: Date | string
  deletedBy?: string
  syncProjectDates: boolean
}

export class Retainer implements IRetainer {
  id: string
  startDate: Date | string
  endDate?: Date | string
  timeBudget: number
  retainerPeriod: string //set manually
  timezone: string //set manually
  rollOverUnderspentBudget: boolean
  rollOverOverspentBudget: boolean
  projectId: string
  isExternal: boolean
  retainerBudgets: RetainerBudget[]
  createdOn: Date | string
  createdBy: string
  updatedOn: Date | string
  updatedBy: string
  deletedOn: Date | string
  deletedBy: string
  syncProjectDates: boolean

  /**
   * Maps retainer fields while preserving the saved timezone.
   * @param {Partial<IRetainer>} [init] The retainer fields.
   */
  constructor(init?: Partial<IRetainer>) {
    Object.assign(this, init)
    this.timezone = init?.timezone || 'Etc/UTC'
    this.retainerPeriod = 'monthly'
    normalizeRetainerDates(this)

    if (this.retainerBudgets?.length) {
      this.retainerBudgets = this.retainerBudgets.map(budget => new RetainerBudget(budget))
    }
  }

  /**
   * Checks whether the current date falls within the date range
   * of the retainer (startDate and endDate) to determine if this is the current retainer
   * @returns {boolean}
   */
  isCurrentRetainer(): boolean {
    const today = convertToTimezone(new Date(), this.timezone)

    try {
      if (this.startDate && this.endDate) {
        return isWithinRange(today, this.startDate, this.endDate)
      }

      if (this.startDate) {
        return isSameYear(today, this.startDate) && isSameMonth(today, this.startDate)
      }

      if (this.endDate) {
        return isSameYear(today, this.endDate) && isSameMonth(today, this.endDate)
      }
    } catch (e) {
      console.log('Error: ', e)
    }

    return false
  }

  /**
   * Calculates the total retainer budget up to and including the current month.
   * Uses the base retainer monthly timeBudget and multiplies it by the number of
   * months from the retainer start (inclusive) up to today (or the retainer end date,
   * whichever comes first).
   *
   * This intentionally ignores the per-month retainerBudgets (which already include
   * roll-over effects) so that the cumulative budget is based on the contract's
   * base monthly budget, independent of how hours are rolled between months.
   * @returns {number} The total budget up to today (in hours).
   */
  budgetUntilToday(): number {
    // Need a valid period and base monthly timeBudget
    if (!this.startDate || !this.endDate || !this.timeBudget) {
      return 0
    }

    const start = parseRetainerDate(this.startDate)
    const today = convertToTimezone(new Date(), this.timezone)
    const endDate = parseRetainerDate(this.endDate)
    // Use the earlier of today or the end date so we do not count future months.
    const end = endDate < today ? endDate : today

    const totalMonthsInclusive =
      (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1

    return Math.max(totalMonthsInclusive, 0) * this.timeBudget
  }

  /**
   * Returns the total tracked duration on the retainer
   * @returns {number}
   */
  totalTrackedDuration(): number {
    return this.retainerBudgets.reduce((total, budget) => total + budget.trackedDuration, 0)
  }
}
